// The immunisation schedule and due list (docs/plans/phase2-maternity-mch.md §4.5).
//
// TODO(clinical-verify) M16: the schedule (which vaccine, which dose, at what age) is the
// clinical lead's, entered in Settings, and empty by default. Nothing here knows a vaccine
// or an age: an empty schedule gives "not configured", never a guessed due date. There are
// no catch-up or minimum-interval rules.

import { addDays, compareDays, formatIsoDay, type CalendarDay } from '../time.ts';

export const SCHEDULE_DOSE_MAX = 20;
/** Twenty years in days: a data-entry bound, not a clinical age. */
export const SCHEDULE_AGE_DAYS_MAX = 7300;

export const DOSE_PLACES = ['here', 'elsewhere'] as const;
export type DosePlace = (typeof DOSE_PLACES)[number];

export const DOSE_PLACE_LABELS: Readonly<Record<DosePlace, string>> = {
  here: 'Given here',
  elsewhere: 'Given elsewhere (from the card)',
};

export interface ImmunisationScheduleEntry {
  readonly vaccineId: string;
  readonly doseNumber: number;
  readonly dueAgeDays: number;
}

export type ImmunisationScheduleErrorCode =
  'schedule.dose_invalid' | 'schedule.age_invalid' | 'schedule.duplicate_dose';

export function checkImmunisationSchedule(
  entries: readonly ImmunisationScheduleEntry[],
): ImmunisationScheduleErrorCode | null {
  const seen = new Set<string>();
  for (const entry of entries) {
    if (
      !Number.isInteger(entry.doseNumber) ||
      entry.doseNumber < 1 ||
      entry.doseNumber > SCHEDULE_DOSE_MAX
    ) {
      return 'schedule.dose_invalid';
    }
    if (
      !Number.isInteger(entry.dueAgeDays) ||
      entry.dueAgeDays < 0 ||
      entry.dueAgeDays > SCHEDULE_AGE_DAYS_MAX
    ) {
      return 'schedule.age_invalid';
    }
    const key = `${entry.vaccineId}#${String(entry.doseNumber)}`;
    if (seen.has(key)) return 'schedule.duplicate_dose';
    seen.add(key);
  }
  return null;
}

export type DueState = 'given' | 'due' | 'upcoming';

export interface DueEntry {
  readonly vaccineId: string;
  readonly doseNumber: number;
  readonly dueOn: string;
  readonly state: DueState;
}

export type DueList =
  | { state: 'not_configured' }
  | { state: 'no_birth_date' }
  | { state: 'listed'; entries: DueEntry[] };

/**
 * Each scheduled dose: given (a dose with that vaccine and number is recorded), due (the
 * configured age has been reached), or upcoming. Ordered by due date, then vaccine and dose.
 */
export function dueList(
  schedule: readonly ImmunisationScheduleEntry[],
  given: readonly { vaccineId: string; doseNumber: number }[],
  birthDate: CalendarDay | null,
  today: CalendarDay,
): DueList {
  if (schedule.length === 0) return { state: 'not_configured' };
  if (birthDate === null) return { state: 'no_birth_date' };
  const givenKeys = new Set(given.map((g) => `${g.vaccineId}#${String(g.doseNumber)}`));
  const entries = schedule
    .map((entry) => {
      const due = addDays(birthDate, entry.dueAgeDays);
      const key = `${entry.vaccineId}#${String(entry.doseNumber)}`;
      const state: DueState = givenKeys.has(key)
        ? 'given'
        : compareDays(due, today) <= 0
          ? 'due'
          : 'upcoming';
      return { entry, due, state };
    })
    .toSorted(
      (a, b) =>
        compareDays(a.due, b.due) ||
        a.entry.vaccineId.localeCompare(b.entry.vaccineId) ||
        a.entry.doseNumber - b.entry.doseNumber,
    );
  return {
    state: 'listed',
    entries: entries.map(({ entry, due, state }) => ({
      vaccineId: entry.vaccineId,
      doseNumber: entry.doseNumber,
      dueOn: formatIsoDay(due),
      state,
    })),
  };
}
